import * as THREE from 'three';
import { createMetalMaterial } from '../utils/materials';
import { tween, Easing } from '../utils/animate';
import { SampleBox, BoxData } from './SampleBox';

export interface ShelfData {
  id: string;
  label: string;
  position: number;
  boxes: BoxData[];
}

const SHELF_WIDTH = 0.7;
const SHELF_DEPTH = 0.55;
const SHELF_HEIGHT = 0.01;

export class Shelf {
  public mesh: THREE.Group;
  public shelfData: ShelfData;
  public boxes: SampleBox[] = [];
  public isExtended = false;
  private slideGroup: THREE.Group;

  constructor(data: ShelfData) {
    this.shelfData = data;
    this.mesh = new THREE.Group();
    this.slideGroup = new THREE.Group();
    this.mesh.userData = { type: 'shelf', id: data.id, shelfRef: this };

    this.buildShelf();
    this.buildBoxes();
    // Y pozíciót a Freezer állítja be
  }

  private buildShelf(): void {
    const metalMat = createMetalMaterial(0x667788);

    // Shelf platform
    const platformGeo = new THREE.BoxGeometry(SHELF_WIDTH, SHELF_HEIGHT, SHELF_DEPTH);
    const platform = new THREE.Mesh(platformGeo, metalMat);
    platform.receiveShadow = true;
    platform.castShadow = true;
    this.slideGroup.add(platform);

    // Rails on sides
    const railGeo = new THREE.BoxGeometry(0.008, 0.02, SHELF_DEPTH);
    const leftRail = new THREE.Mesh(railGeo, metalMat);
    leftRail.position.set(-SHELF_WIDTH / 2 + 0.004, 0.015, 0);
    this.slideGroup.add(leftRail);

    const rightRail = new THREE.Mesh(railGeo, metalMat);
    rightRail.position.set(SHELF_WIDTH / 2 - 0.004, 0.015, 0);
    this.slideGroup.add(rightRail);

    // Handle at front
    const handleGeo = new THREE.BoxGeometry(0.08, 0.015, 0.012);
    const handle = new THREE.Mesh(handleGeo, createMetalMaterial(0x99aabb));
    handle.position.set(0, 0.01, SHELF_DEPTH / 2 + 0.006);
    this.slideGroup.add(handle);

    this.mesh.add(this.slideGroup);
  }

  private buildBoxes(): void {
    const boxesPerRow = 3;
    const spacing = SHELF_WIDTH / (boxesPerRow + 0.5);

    for (const boxData of this.shelfData.boxes) {
      const box = new SampleBox(boxData);
      const col = boxData.col ?? this.boxes.length % boxesPerRow;
      const x = (col - (boxesPerRow - 1) / 2) * spacing;
      box.mesh.position.set(x, SHELF_HEIGHT / 2, 0);
      this.slideGroup.add(box.mesh);
      this.boxes.push(box);
    }
  }

  extend(): Promise<void> {
    if (this.isExtended) return Promise.resolve();
    this.isExtended = true;
    return new Promise((resolve) => {
      tween({
        from: 0,
        to: SHELF_DEPTH * 0.8,
        duration: 600,
        easing: Easing.easeOutCubic,
        onUpdate: (v) => {
          this.slideGroup.position.z = v;
        },
        onComplete: resolve,
      });
    });
  }

  retract(): Promise<void> {
    if (!this.isExtended) return Promise.resolve();
    this.isExtended = false;
    return new Promise((resolve) => {
      tween({
        from: this.slideGroup.position.z,
        to: 0,
        duration: 500,
        easing: Easing.easeOutCubic,
        onUpdate: (v) => {
          this.slideGroup.position.z = v;
        },
        onComplete: resolve,
      });
    });
  }

  updatePulse(time: number): void {
    for (const box of this.boxes) {
      box.updatePulse(time);
    }
  }
}
